import { useSearchParams } from "react-router-dom";
import {
  Check,
  Clapperboard,
  Image as ImageIcon,
  Inbox,
  MessageSquareQuote,
  RefreshCw,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../lib/api";
import { useAdmin } from "./AdminShell";

interface ReviewQueueItem {
  kind: "submission" | "report";
  id: string;
  eventId: string;
  authorName: string;
  content: string;
  state: "pending" | "approved" | "rejected";
  createdAt: string;
  media: null | {
    id: string;
    originalFilename: string;
    derivatives: Array<{ url: string }>;
  };
  video: null | {
    id: string;
    state: string;
    title: string;
    thumbnailUrl: string | null;
  };
}

export function AdminApprovalsPage({
  embedded = false,
  eventId,
}: { embedded?: boolean; eventId?: string } = {}) {
  const { data, refresh } = useAdmin();
  const [params] = useSearchParams();
  const selectedEventId = eventId ?? params.get("event");
  const [items, setItems] = useState<ReviewQueueItem[]>([]);
  const [filter, setFilter] = useState<ReviewQueueItem["state"]>("pending");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setItems(await apiFetch<ReviewQueueItem[]>("/api/admin/review-queue"));
      setError("");
    } catch {
      setError(
        "Einreichungen konnten nicht geladen werden. Bitte aktualisiere die Ansicht.",
      );
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const visible = useMemo(
    () =>
      items.filter(
        (item) =>
          item.state === filter &&
          (!selectedEventId || item.eventId === selectedEventId),
      ),
    [items, filter, selectedEventId],
  );

  const review = async (
    item: ReviewQueueItem,
    action: "approve" | "reject",
  ) => {
    setBusy(item.id);
    try {
      const url =
        item.kind === "report"
          ? `/api/admin/posts/${item.id}/review`
          : `/api/admin/submissions/${item.id}/review`;
      await apiFetch(url, {
        method: "POST",
        body: JSON.stringify({ action }),
      });
      await Promise.all([load(), refresh()]);
    } catch {
      setError(
        "Die Freigabe konnte nicht gespeichert werden. Bitte versuche es erneut.",
      );
    } finally {
      setBusy("");
    }
  };

  return (
    <div
      className={`admin-page approvals-page ${embedded ? "dashboard-approvals" : ""}`}
    >
      <header className="admin-page-header">
        <div>
          {embedded ? <h2>Freigaben</h2> : <h1>Freigaben</h1>}
          {!embedded && (
            <p>
              Prüfe Einreichungen aus dem Publikum und Feldberichte der
              Reporter, bevor sie live gehen.
            </p>
          )}
        </div>
        <button
          className="icon-action"
          onClick={() => void load()}
          aria-label="Aktualisieren"
        >
          <RefreshCw />
        </button>
      </header>
      {error && <p role="alert">{error}</p>}
      <div className="approval-filters" hidden={embedded}>
        {(["pending", "approved", "rejected"] as const).map((value) => (
          <button
            key={value}
            className={filter === value ? "active" : ""}
            aria-pressed={filter === value}
            onClick={() => setFilter(value)}
          >
            {value === "pending"
              ? "Offen"
              : value === "approved"
                ? "Freigegeben"
                : "Abgelehnt"}
            <span>{items.filter((item) => item.state === value).length}</span>
          </button>
        ))}
      </div>
      <section className="approval-list">
        {visible.map((item) => {
          const event = data.events.find((own) => own.id === item.eventId);
          const image = item.media?.derivatives?.[0]?.url;
          const video = item.video;
          const videoThumb = video?.thumbnailUrl ?? undefined;
          return (
            <article key={`${item.kind}-${item.id}`}>
              <header>
                <span className="submission-avatar">
                  {item.authorName.slice(0, 1).toUpperCase()}
                </span>
                <div>
                  <strong>{item.authorName}</strong>
                  <small>
                    {event?.title ?? "Event"} ·{" "}
                    {new Intl.DateTimeFormat("de", {
                      hour: "2-digit",
                      minute: "2-digit",
                    }).format(new Date(item.createdAt))}
                    {item.kind === "report" ? " · Reporter" : ""}
                  </small>
                </div>
                {video ? (
                  <Clapperboard />
                ) : image ? (
                  <ImageIcon />
                ) : (
                  <MessageSquareQuote />
                )}
              </header>
              {video ? (
                <figure className="approval-video">
                  {videoThumb ? (
                    <img src={videoThumb} alt="" loading="lazy" />
                  ) : null}
                  <figcaption>
                    {video.title || "Video"}
                    {video.state && video.state !== "ready"
                      ? ` · Video ${video.state === "failed" ? "fehlgeschlagen" : "wird verarbeitet"}`
                      : ""}
                  </figcaption>
                </figure>
              ) : image ? (
                <img
                  className="approval-image"
                  src={image}
                  alt={`Foto von ${item.authorName}`}
                  loading="lazy"
                />
              ) : null}
              <blockquote>{item.content || "(Kein Text)"}</blockquote>
              {item.state === "pending" && (
                <footer>
                  <button
                    disabled={!!busy}
                    onClick={() => void review(item, "reject")}
                  >
                    <X size={17} /> Ablehnen
                  </button>
                  <button
                    disabled={!!busy}
                    onClick={() => void review(item, "approve")}
                    className="button-live"
                  >
                    <Check size={17} /> Veröffentlichen
                  </button>
                </footer>
              )}
            </article>
          );
        })}
        {!visible.length && !error && (
          <div className="admin-empty">
            <Inbox />
            {embedded ? (
              <h3>Alles gesichtet.</h3>
            ) : (
              <h2>Hier ist gerade nichts.</h2>
            )}
            <p>
              {filter === "pending"
                ? "Alle Beiträge sind geprüft – sehr gut."
                : "Keine Einträge in dieser Ansicht."}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
